import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  ButtonGroup
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  QrCodeScanner as QrCodeScannerIcon,
  LocalShipping as TruckIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

// Services
import { updateBundleStatusByQR } from '../services/technicianInventoryService';

const QRScan = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);

  const qrCode = searchParams.get('qr');
  const status = searchParams.get('status') || 'on_truck';

  useEffect(() => {
    if (qrCode) {
      handleQRScan();
    }
  }, [qrCode]);

  const handleQRScan = async () => {
    if (!qrCode) {
      setError('No QR code provided');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setErrorDetails(null);

      const response = await updateBundleStatusByQR(qrCode, status);
      setResult(response);
    } catch (err) {
      console.error('Error updating bundle status:', err);

      // Handle enhanced error responses
      if (err.error) {
        setErrorDetails(err);
        setError(err.message);
      } else {
        setError(err.message || 'Failed to update bundle status');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusDisplayText = (status) => {
    const statusMap = {
      'assigned': 'Assigned',
      'on_truck': 'On Truck',
      'used': 'Used',
      'need_to_return': 'Need to Return',
      'returned': 'Returned'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'assigned': 'primary',
      'on_truck': 'info',
      'used': 'success',
      'need_to_return': 'warning',
      'returned': 'default'
    };
    return colorMap[status] || 'default';
  };

  const renderEnhancedError = () => {
    if (!errorDetails) {
      return (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Update Failed
          </Typography>
          {error}
        </Alert>
      );
    }

    const { error: errorType, message, ...details } = errorDetails;

    // Determine alert severity and icon based on error type
    let severity = 'error';
    let icon = <ErrorIcon />;

    if (errorType === 'already_in_status' || errorType === 'invalid_transition') {
      severity = 'warning';
      icon = <WarningIcon />;
    } else if (errorType === 'mixed_bundle_status') {
      severity = 'info';
      icon = <InfoIcon />;
    }

    return (
      <Alert severity={severity} sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          {icon}
          <Typography variant="h6">
            {errorType === 'qr_not_found' && 'QR Code Not Found'}
            {errorType === 'already_in_status' && 'Already in Status'}
            {errorType === 'invalid_transition' && 'Invalid Status Change'}
            {errorType === 'item_returned' && 'Item Already Returned'}
            {errorType === 'mixed_bundle_status' && 'Bundle Status Conflict'}
            {!['qr_not_found', 'already_in_status', 'invalid_transition', 'item_returned', 'mixed_bundle_status'].includes(errorType) && 'Update Failed'}
          </Typography>
        </Box>

        <Typography variant="body1" sx={{ mb: 2 }}>
          {message}
        </Typography>

        {/* Show current status and available actions */}
        {details.current_status && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Current Status:</strong>{' '}
              <Chip
                label={details.current_status_display || getStatusDisplayText(details.current_status)}
                color={getStatusColor(details.current_status)}
                size="small"
              />
            </Typography>

            {details.available_actions && details.available_actions.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Available Actions:</strong>
                </Typography>
                <ButtonGroup size="small" variant="outlined">
                  {details.available_actions.map((action) => (
                    <Button
                      key={action}
                      onClick={() => {
                        const newUrl = new URL(window.location);
                        newUrl.searchParams.set('status', action);
                        window.location.href = newUrl.toString();
                      }}
                    >
                      {getStatusDisplayText(action)}
                    </Button>
                  ))}
                </ButtonGroup>
              </Box>
            )}
          </Box>
        )}

        {/* Show item details for context */}
        {details.item_details && details.item_details.length > 0 && (
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Item Details:
              </Typography>
              <List dense>
                {details.item_details.slice(0, 3).map((item, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemText
                      primary={`${item.part_number} - ${item.description}`}
                      secondary={`Quantity: ${item.quantity}`}
                    />
                  </ListItem>
                ))}
                {details.item_details.length > 3 && (
                  <Typography variant="caption" color="text.secondary">
                    ... and {details.item_details.length - 3} more items
                  </Typography>
                )}
              </List>
            </CardContent>
          </Card>
        )}

        {/* Show bundle summary for mixed status errors */}
        {details.bundle_summary && (
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Bundle Status Summary:
              </Typography>
              <Typography variant="body2">
                Total Items: {details.bundle_summary.total_items}
              </Typography>
              {Object.entries(details.bundle_summary.status_breakdown || {}).map(([status, count]) => (
                <Typography key={status} variant="body2">
                  {getStatusDisplayText(status)}: {count} item{count !== 1 ? 's' : ''}
                </Typography>
              ))}
            </CardContent>
          </Card>
        )}
      </Alert>
    );
  };

  if (!qrCode) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <QrCodeScannerIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            QR Code Scanner
          </Typography>
          <Typography variant="body1" color="text.secondary">
            This page is designed to be accessed by scanning a QR code from a technician bundle label.
          </Typography>
          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              onClick={() => navigate('/inventory/scan')}
            >
              Go to Scan Inventory
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <QrCodeScannerIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="h5" gutterBottom>
            Bundle Status Update
          </Typography>
          <Typography variant="body2" color="text.secondary">
            QR Code: {qrCode.length > 50 ? `${qrCode.substring(0, 50)}...` : qrCode}
          </Typography>
          {qrCode.includes(',') && (
            <Typography variant="caption" color="text.secondary">
              Bundle QR Code (Multiple Parts)
            </Typography>
          )}
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Updating status...
            </Typography>
          </Box>
        )}

        {error && renderEnhancedError()}

        {result && (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon />
                <Typography variant="h6">
                  Status Updated Successfully!
                </Typography>
              </Box>
            </Alert>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TruckIcon color="primary" />
                  <Typography variant="h6">
                    Update Summary
                  </Typography>
                </Box>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Items Updated:</strong> {result.updated_count}
                </Typography>

                {result.technician && (
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    <strong>Technician:</strong> {result.technician}
                  </Typography>
                )}

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>New Status:</strong>{' '}
                  <Chip
                    label={getStatusDisplayText(status)}
                    color={getStatusColor(status)}
                    size="small"
                  />
                </Typography>

                {result.is_persistent && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Note:</strong> This QR code can be used multiple times for future status updates.
                    </Typography>
                  </Alert>
                )}

                {!result.is_persistent && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Note:</strong> This QR code was for one-time use only and is now expired.
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {result.records && result.records.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Updated Parts
                  </Typography>
                  <List dense>
                    {result.records.map((record, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemText
                            primary={`${record.part_number} - ${record.description}`}
                            secondary={`Quantity: ${record.quantity} | Status: ${getStatusDisplayText(record.new_status)}`}
                          />
                        </ListItem>
                        {index < result.records.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                variant="contained"
                onClick={() => navigate('/inventory/scan')}
                sx={{ mr: 2 }}
              >
                Back to Scan Inventory
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/technicians')}
              >
                View Technicians
              </Button>
            </Box>
          </Box>
        )}

        {!loading && !result && !error && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Ready to update status to: <strong>{getStatusDisplayText(status)}</strong>
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleQRScan}
              startIcon={<AssignmentIcon />}
            >
              Update Status
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default QRScan;
